/**
 * @lodex-pro/web-admin/features/ops/alerts-hooks — #56 PR 2c (DEC-363): the ops alert
 * admin reads and writes (GET/PATCH/POST/DELETE /v1/platform/ops/alerts/*, PR 2a).
 *
 * Reads poll like the rest of the Operations page. Every write invalidates the alert
 * queries so the tabs show the saved state.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import { OPS_POLL } from './hooks';

export type AlertSeverity = 'WARNING' | 'CRITICAL';
export type IncidentStatus = 'OPEN' | 'ACKED' | 'RESOLVED';
export type RuleType =
  | 'TENANT_ERROR_SPIKE'
  | 'UNCAUGHT_ERROR'
  | 'CATALOG_SYNC_FAILED'
  | 'EMAIL_FAILURES'
  | 'PDF_RENDER_FAILURES'
  | 'QUEUE_BACKLOG'
  | 'SCHEDULER_STALE'
  | 'API_5XX_RATE'
  // 13.10 (M1): no params; the incident names the company, the key and the count.
  | 'OPERATOR_API_WRITE_QUOTA';

export type RuleParams = Record<string, number | Record<string, number>>;

export interface AlertRule {
  readonly id: string;
  readonly ruleType: RuleType;
  readonly name: string;
  readonly enabled: boolean;
  readonly params: RuleParams;
  readonly cooldownWarningMinutes: number;
  readonly cooldownCriticalMinutes: number;
  readonly clearAfterEvals: number;
  readonly snoozeUntil: string | null;
  readonly updatedAt: string | null;
  readonly liveIncidents: number;
}

export interface QuietHours {
  readonly start: string;
  readonly end: string;
  readonly timeZone: string;
}

export interface AlertRecipient {
  readonly id: string;
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly emailEnabled: boolean;
  readonly minSeverityEmail: AlertSeverity;
  readonly quietHours: QuietHours | null;
}

export interface RecipientCandidate {
  readonly userId: string;
  readonly name: string;
  readonly email: string;
}

export interface AlertIncident {
  readonly id: string;
  readonly ruleId: string;
  readonly ruleName: string;
  readonly ruleType: RuleType;
  readonly dedupeKey: string;
  readonly status: IncidentStatus;
  readonly severity: AlertSeverity;
  readonly tenantId: string | null;
  readonly tenantName: string | null;
  readonly firstFiredAt: string;
  readonly lastFiredAt: string;
  readonly lastValue: number | null;
  readonly summary: string;
  readonly lastNotifiedAt: string | null;
  readonly ackedBy: string | null;
  readonly ackedAt: string | null;
  readonly resolvedBy: string | null;
  readonly resolvedAt: string | null;
  readonly resolveReason: 'AUTO_CLEARED' | 'MANUAL' | 'EXPIRED' | null;
  readonly note: string | null;
}

export interface IncidentNotification {
  readonly id: string;
  readonly kind: 'OPENED' | 'REMINDER' | 'ESCALATED' | 'RESOLVED' | 'TEST';
  readonly severity: AlertSeverity;
  readonly status: 'SENT' | 'FAILED' | 'SUPPRESSED';
  readonly at: string;
  readonly recipientName: string | null;
  readonly error: string | null;
}

const BASE = '/v1/platform/ops/alerts';
export const ALERTS_KEY = ['platform', 'ops', 'alerts'] as const;

export function useAlertSettings(): ReturnType<typeof useApiQuery<{ alertsEnabled: boolean }>> {
  return useApiQuery(useApiOpts(), `${BASE}/settings`, {
    queryKey: [...ALERTS_KEY, 'settings'],
    ...OPS_POLL,
  });
}

export function useAlertRules(): ReturnType<typeof useApiQuery<{ rules: AlertRule[] }>> {
  return useApiQuery(useApiOpts(), `${BASE}/rules`, {
    queryKey: [...ALERTS_KEY, 'rules'],
    ...OPS_POLL,
  });
}

export function useAlertRecipients(): ReturnType<
  typeof useApiQuery<{ recipients: AlertRecipient[] }>
> {
  return useApiQuery(useApiOpts(), `${BASE}/recipients`, {
    queryKey: [...ALERTS_KEY, 'recipients'],
    ...OPS_POLL,
  });
}

export function useRecipientCandidates(): ReturnType<
  typeof useApiQuery<{ candidates: RecipientCandidate[] }>
> {
  return useApiQuery(useApiOpts(), `${BASE}/recipient-candidates`, {
    queryKey: [...ALERTS_KEY, 'candidates'],
    staleTime: 30_000,
  });
}

export type IncidentFilter = 'LIVE' | 'RESOLVED' | 'ALL';

export function useAlertIncidents(
  status: IncidentFilter,
): ReturnType<typeof useApiQuery<{ incidents: AlertIncident[]; nextCursor: string | null }>> {
  const path = `${BASE}/incidents?status=${status}`;
  return useApiQuery(useApiOpts(), path, {
    queryKey: [...ALERTS_KEY, 'incidents', status],
    ...OPS_POLL,
  });
}

export function useAlertIncident(
  id: string,
): ReturnType<
  typeof useApiQuery<{ incident: AlertIncident; notifications: IncidentNotification[] }>
> {
  return useApiQuery(useApiOpts(), `${BASE}/incidents/${id}`, {
    queryKey: [...ALERTS_KEY, 'incident', id],
    ...OPS_POLL,
  });
}

/** One write, with the alert queries refreshed after it succeeds. */
export function useAlertWrite<TBody, TRes = unknown>(
  path: string,
  method: 'POST' | 'PATCH' | 'DELETE',
): {
  readonly run: (body: TBody, done?: (res: TRes) => void, failed?: (err: unknown) => void) => void;
  readonly isPending: boolean;
} {
  const qc = useQueryClient();
  const m = useApiMutation<TBody, TRes>(useApiOpts(), `${BASE}${path}`, method);
  return {
    run: (body, done, failed): void =>
      m.mutate(body, {
        onSuccess: (res: TRes) => {
          void qc.invalidateQueries({ queryKey: ALERTS_KEY });
          done?.(res);
        },
        onError: (err: unknown) => failed?.(err),
      }),
    isPending: m.isPending,
  };
}
